import BusinessIcon from '@mui/icons-material/Business';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';
import CheckBoxSharpIcon from '@mui/icons-material/CheckBoxSharp';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { Breadcrumbs, Checkbox, Stack, Typography } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  result: { url: string; keyword: string };
  isEven: boolean;
  selected: boolean;

  handleSelectionChange(url: string): () => void;
}

const LinkedinSearchProjectItem: React.FC<Props> = ({ result, isEven, selected, handleSelectionChange }) => {
  const { classes, cx } = useStyles({ isEven });

  const keywordSplit = result.keyword.split(';');
  const keywordObj = { firstName: keywordSplit[0] || undefined, lastName: keywordSplit[1] || undefined, company: keywordSplit[2] || undefined };

  return (
    <div className={classes.searchProject}>
      <Stack direction={'column'} gap={1} style={{ width: '100%' }}>
        <Breadcrumbs className={classes.breadcrumbs}>
          {!_.isNil(keywordObj.firstName) || !_.isNil(keywordObj.lastName) ? (
            <div className={classes.flexContainer}>
              <PersonIcon className={classes.icon} />
              {!_.isNil(keywordObj.firstName) && !_.isNil(keywordObj.lastName) ? (
                <Typography className={classes.keyword}>{`${keywordObj.firstName} ${keywordObj.lastName}`}</Typography>
              ) : !_.isNil(keywordObj.firstName) ? (
                <Typography className={classes.keyword}>{keywordObj.firstName}</Typography>
              ) : !_.isNil(keywordObj.lastName) ? (
                <Typography className={classes.keyword}>{keywordObj.lastName}</Typography>
              ) : null}
            </div>
          ) : null}
          {!_.isNil(keywordObj.company) ? (
            <div className={classes.flexContainer}>
              <BusinessIcon className={classes.icon} />
              <Typography className={classes.keyword}>{keywordObj.company}</Typography>
            </div>
          ) : null}
        </Breadcrumbs>
        <Stack direction={'row'} alignItems={'center'}>
          <LinkIcon className={classes.icon} />
          <PaiLink text={result.url} size={'sm'} variant={'primary'} underline={'always'} href={result.url} target={'_blank'} />
          <OpenInNewIcon className={cx(classes.icon, 'right-placement', 'dense')} />
        </Stack>
      </Stack>
      <Checkbox
        className={classes.checkbox}
        checkedIcon={<CheckBoxSharpIcon />}
        icon={<CheckBoxOutlineBlankSharpIcon />}
        checked={selected}
        disableRipple={true}
        onChange={handleSelectionChange(result.url)}
      />
    </div>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  searchProject: {
    position: 'relative',
    width: '100%',
    padding: '8px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
    paddingRight: '48px',
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbs: {
    width: '100%',
    '& ol': {
      flexWrap: 'wrap',
      gap: '8px',
    },
    '& .MuiBreadcrumbs-separator': {
      color: theme.palette.greys.medium,
      height: theme.typography.pxToRem(20),
      [theme.breakpoints.down(400)]: {
        height: theme.typography.pxToRem(16),
      },
    },
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  keyword: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    lineHeight: 1,
    marginTop: '4px',
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: '8px',
    color: theme.palette.greys.medium,
    '&.right-placement': {
      color: theme.palette.greys.medium,
      marginRight: 0,
      marginLeft: '8px',
    },
    '&.dense': {
      fontSize: theme.typography.pxToRem(16),
    },
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      '&.dense': {
        fontSize: theme.typography.pxToRem(12),
      },
    },
  },
  checkbox: {
    position: 'absolute',
    right: '8px',
    color: theme.palette.primary.main,
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(20),
    },
    [theme.breakpoints.down(400)]: {
      '& .MuiSvgIcon-root': {
        fontSize: theme.typography.pxToRem(16),
      },
    },
  },
}));

export default LinkedinSearchProjectItem;
